import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { BOAT_ENGINE_INSTALL_OPTIONS, BOAT_ENGINE_FUEL_OPTIONS } from "@/lib/policyConstants";

/**
 * Editor per motori aggiuntivi di polizza natante (multi-motore).
 * Il motore #1 (principale) è nei campi top-level `boat_engine_*` della polizza.
 * Questo componente gestisce solo i motori dal #2 in poi (`boat_engines_extra`).
 * Utile per catamarani, gommoni a motori gemelli, ecc.
 *
 * Props:
 *  - extraEngines: array di motori aggiuntivi (senza il primario)
 *  - onChange: (nextArray) => void
 *  - disabled: bool
 */
export function BoatEnginesEditor({ extraEngines = [], onChange, disabled = false }) {
  const engines = extraEngines || [];

  const add = () => {
    onChange([
      ...engines,
      { maker: "", model: "", serial: "", install: "", fuel: "", kw: "", mass_kg: "", declaration_number: "", declaration_date: "" },
    ]);
  };
  const remove = (idx) => {
    onChange(engines.filter((_, i) => i !== idx));
  };
  const update = (idx, patch) => {
    onChange(engines.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  const total = useMemo(() => engines.length + 1, [engines.length]);

  return (
    <div className="space-y-3" data-testid="boat-engines-editor">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-500">
          Motore principale: dati inseriti sopra. <strong>{total > 1 ? `${total - 1} motore/i aggiuntivo/i configurato/i.` : "Aggiungi motori aggiuntivi (es. catamarano, gommoni a motori gemelli)."}</strong>
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="boat-engine-add"
          onClick={add}
          disabled={disabled}
          className="border-cyan-500 text-cyan-700 hover:bg-cyan-50"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi motore
        </Button>
      </div>

      {engines.map((e, idx) => (
        <div key={idx} className="border border-cyan-200 bg-cyan-50/40 rounded-md p-3 space-y-2" data-testid={`boat-engine-row-${idx}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-cyan-800">⚙️ Motore #{idx + 2}</span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid={`boat-engine-remove-${idx}`}
              onClick={() => remove(idx)}
              disabled={disabled}
              className="h-7 px-2 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <div>
              <Label className="text-[11px]">Costruttore</Label>
              <Input data-testid={`boat-engine-maker-${idx}`} value={e.maker || ""} onChange={(ev) => update(idx, { maker: ev.target.value })} disabled={disabled} />
            </div>
            <div>
              <Label className="text-[11px]">Modello</Label>
              <Input data-testid={`boat-engine-model-${idx}`} value={e.model || ""} onChange={(ev) => update(idx, { model: ev.target.value })} disabled={disabled} />
            </div>
            <div>
              <Label className="text-[11px]">N° matricola motore</Label>
              <Input data-testid={`boat-engine-serial-${idx}`} value={e.serial || ""} onChange={(ev) => update(idx, { serial: ev.target.value })} disabled={disabled} />
            </div>
            <div>
              <Label className="text-[11px]">Installazione</Label>
              <Select value={e.install || "_none"} onValueChange={(v) => update(idx, { install: v === "_none" ? "" : v })} disabled={disabled}>
                <SelectTrigger data-testid={`boat-engine-install-${idx}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Non specificato —</SelectItem>
                  {BOAT_ENGINE_INSTALL_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Combustibile</Label>
              <Select value={e.fuel || "_none"} onValueChange={(v) => update(idx, { fuel: v === "_none" ? "" : v })} disabled={disabled}>
                <SelectTrigger data-testid={`boat-engine-fuel-${idx}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— Non specificato —</SelectItem>
                  {BOAT_ENGINE_FUEL_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Potenza (Kw)</Label>
              <Input type="number" step="0.01" data-testid={`boat-engine-kw-${idx}`} value={e.kw || ""} onChange={(ev) => update(idx, { kw: ev.target.value })} disabled={disabled} />
            </div>
            <div>
              <Label className="text-[11px]">Massa (kg)</Label>
              <Input type="number" step="0.1" data-testid={`boat-engine-mass-${idx}`} value={e.mass_kg || ""} onChange={(ev) => update(idx, { mass_kg: ev.target.value })} disabled={disabled} />
            </div>
            <div>
              <Label className="text-[11px]">N° dichiarazione</Label>
              <Input data-testid={`boat-engine-decl-num-${idx}`} value={e.declaration_number || ""} onChange={(ev) => update(idx, { declaration_number: ev.target.value })} disabled={disabled} />
            </div>
            <div>
              <Label className="text-[11px]">Data dichiarazione</Label>
              <Input type="date" data-testid={`boat-engine-decl-date-${idx}`} value={e.declaration_date || ""} onChange={(ev) => update(idx, { declaration_date: ev.target.value })} disabled={disabled} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Costruisce il payload `boat_engines[]` da inviare al backend combinando il motore
 * principale (top-level `boat_engine_*`) con gli extras. Ritorna undefined se non c'è
 * nessun dato motore (né principale né extra) → non toccare il campo lato server.
 */
export function buildBoatEnginesPayload(form) {
  const primaryFields = [
    form.boat_engine_maker, form.boat_engine_model, form.boat_engine_serial,
    form.boat_engine_install, form.boat_engine_fuel, form.boat_engine_kw, form.boat_engine_mass_kg,
    form.boat_declaration_number, form.boat_declaration_date,
  ];
  const hasPrimary = primaryFields.some((v) => v != null && String(v).trim() !== "");
  const extras = (form.boat_engines_extra || []).filter((e) => e && (
    (e.maker || "").trim() || (e.model || "").trim() || (e.serial || "").trim() ||
    (e.install || "").trim() || (e.fuel || "").trim() || (e.kw || "").trim() ||
    (e.mass_kg || "").trim() || (e.declaration_number || "").trim() || (e.declaration_date || "").trim()
  ));
  if (!hasPrimary && extras.length === 0) return undefined;
  const primary = hasPrimary ? {
    maker: form.boat_engine_maker || null,
    model: form.boat_engine_model || null,
    serial: form.boat_engine_serial || null,
    install: form.boat_engine_install || null,
    fuel: form.boat_engine_fuel || null,
    kw: form.boat_engine_kw || null,
    mass_kg: form.boat_engine_mass_kg || null,
    declaration_number: form.boat_declaration_number || null,
    declaration_date: form.boat_declaration_date || null,
  } : null;
  const clean = (e) => ({
    maker: e.maker || null,
    model: e.model || null,
    serial: e.serial || null,
    install: e.install || null,
    fuel: e.fuel || null,
    kw: e.kw || null,
    mass_kg: e.mass_kg || null,
    declaration_number: e.declaration_number || null,
    declaration_date: e.declaration_date || null,
  });
  return [
    ...(primary ? [primary] : []),
    ...extras.map(clean),
  ];
}
